import {
  TProgressiveTier,
  getDisplayableTiers,
  formatShippingTierLabel,
  formatTierQuantityLabel,
  formatShippingColumnLabel,
  formatDiscountCapLabel,
  isAmountTrigger
} from '@cannect/services/resources/progressive-discounts'
import { formatReal } from '@cannect/utils/formatters'
import type { EligibilityTier } from '@cannect/services/resources/recurrency/types'
import { useState } from 'react'

type Props = {
  tiers: TProgressiveTier[]
  onSelectTier?: (quantity: number) => void
  /** Preço real do produto na PDP (usado para calcular unitFinal por faixa). */
  productPrice?: number
  /** Tipo de gatilho da regra: 'quantity' (unidades) ou 'amount' (R$). */
  triggerType?: string
  /** Escopo da condição da regra: 'targeted' (produtos da marca) ou 'cart' (carrinho todo). */
  conditionScope?: string
  /** Benefício da regra: com 'shipping' ou 'both' a tabela ganha as colunas Produto e Frete. */
  benefitTarget?: string
  /** Teto de unidades com desconto de item (rule.max_discounted_units). Ausente = sem teto. */
  maxDiscountedUnits?: number | null
  /** Produto importado (country ≠ 6, Brasil): rótulos de frete viram "Custo de Importação". */
  isImported?: boolean
  /** Compra Recorrente (007) selecionada: chip "X% OFF + N%" e preço de assinante do serviço (delta B2). */
  subscription?: { subscriberDiscountPercent: number; tiers: EligibilityTier[] }
}

export const ProgressiveDiscountTiers = ({
  tiers,
  onSelectTier,
  productPrice,
  triggerType,
  conditionScope,
  benefitTarget,
  maxDiscountedUnits,
  isImported = true,
  subscription
}: Props) => {
  const displayableTiers = getDisplayableTiers(tiers)
  const [selectedMinQty, setSelectedMinQty] = useState<number | null>(null)
  const isAmount = isAmountTrigger(triggerType)
  // Nota de marca só aparece quando a regra é por valor e escopo é 'targeted'
  const showBrandNote = isAmount && conditionScope === 'targeted'
  const showShippingColumn = benefitTarget === 'shipping' || benefitTarget === 'both'
  // Regra por quantidade com colunas: reserva a coluna de preço em todas as faixas (e no cabeçalho)
  // para Produto/Frete ficarem alinhados mesmo nas faixas sem desconto no produto (sem preço /un).
  const reservePriceColumn = showShippingColumn && !isAmount
  const capLabel = formatDiscountCapLabel(maxDiscountedUnits)
  const isCapped = !!capLabel

  if (!displayableTiers.length) return null

  const handleTierClick = (minQty: number) => {
    setSelectedMinQty(minQty)
    onSelectTier?.(minQty)
  }

  // Faixas amount nunca são clicáveis: clicar colocaria R$ de threshold como unidades no carrinho
  const isClickable = !!onSelectTier && !isAmount

  return (
    <div className="mt-4 w-full rounded-2xl border border-primary-100 bg-primary-50/40 p-4">
      {!isAmount && onSelectTier && (
        <p className="mb-2 text-xs text-neutral-500">Clique em uma faixa para selecionar a quantidade.</p>
      )}

      {showShippingColumn && (
        <div className="mb-1 flex items-center gap-2 px-4 text-[11px] font-semibold uppercase text-neutral-500">
          <span className="flex-1">Quantidade</span>
          <span className="w-20 text-center">Produto</span>
          <span className="w-20 text-center">{isImported ? 'Custo de Importação' : 'Frete'}</span>
          {reservePriceColumn && <span className="w-28 shrink-0" />}
        </div>
      )}

      <div className="flex flex-col gap-2">
        {displayableTiers.map((tier) => {
          const isSelected = !isAmount && selectedMinQty === tier.min_quantity
          const pct = Number(tier.discount_percent) || 0
          const hasItemBenefit = pct > 0
          const hasShippingBenefit = Number(tier.shipping_value) > 0
          // Usa o preço real do produto (não o unit_final_price da API, que é baseado em catálogo)
          const unitFinal = productPrice && hasItemBenefit ? +(productPrice * (1 - pct / 100)).toFixed(2) : null
          // Multiplicar preço por threshold em R$ não tem sentido — exibe total só para quantity.
          // Com teto, só as unidades `discounted_units` (do backend) têm desconto; o restante paga preço cheio.
          const discountedUnits =
            isCapped && tier.discounted_units !== undefined
              ? Math.min(Number(tier.discounted_units), tier.min_quantity)
              : tier.min_quantity
          const totalFinal =
            unitFinal && !isAmount
              ? +(
                  unitFinal * discountedUnits +
                  (productPrice as number) * (tier.min_quantity - discountedUnits)
                ).toFixed(2)
              : null
          // Assinatura: preço médio de assinante da faixa vem pronto do serviço.
          const subscriberTier = subscription?.tiers.find((t) => t.minQuantity === tier.min_quantity)
          const shownUnit = subscriberTier && !isAmount ? subscriberTier.subscriberUnitPrice : unitFinal
          const shownTotal =
            subscriberTier && !isAmount
              ? +(subscriberTier.subscriberUnitPrice * tier.min_quantity).toFixed(2)
              : totalFinal
          const discountLabel = subscription
            ? `${tier.discount_percent}% OFF + ${subscription.subscriberDiscountPercent}%`
            : `${tier.discount_percent}% OFF`

          const Wrapper = isClickable ? 'button' : 'div'
          const wrapperProps = isClickable
            ? {
                type: 'button' as const,
                onClick: () => handleTierClick(tier.min_quantity)
              }
            : {}

          return (
            <Wrapper
              key={tier.min_quantity}
              {...wrapperProps}
              className={[
                'flex w-full items-center justify-between rounded-xl border px-4 py-2.5 text-left transition-colors',
                reservePriceColumn ? 'gap-2' : '',
                isClickable ? 'cursor-pointer' : '',
                isSelected ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-400' : 'border-primary-100 bg-white',
                // Hover so na faixa clicavel: em regra por valor a faixa e informativa e o realce
                // convidaria a um clique que nao existe.
                isClickable && !isSelected ? 'hover:border-primary-300 hover:bg-primary-50/60' : ''
              ]
                .filter(Boolean)
                .join(' ')}>
              {showShippingColumn ? (
                <div className="flex flex-1 items-center gap-2">
                  <span className="flex-1 text-xs font-medium text-neutral-600">
                    {formatTierQuantityLabel(tier, triggerType, { capped: isCapped })}
                  </span>
                  <span className="w-20 text-center text-xs font-semibold text-neutral-800">
                    {hasItemBenefit ? discountLabel : '—'}
                  </span>
                  <span className="w-20 text-center text-xs font-semibold text-neutral-800">
                    {formatShippingColumnLabel(tier)}
                  </span>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-neutral-600">
                    {formatTierQuantityLabel(tier, triggerType, { capped: isCapped })}
                    {showBrandNote && '*'}
                  </span>
                  {hasItemBenefit && (
                    <span className="rounded-full bg-primary-600 px-2 py-0.5 text-xs font-semibold text-white">
                      {discountLabel}
                    </span>
                  )}
                  {hasShippingBenefit && (
                    <span className="rounded-full bg-primary-600 px-2 py-0.5 text-xs font-semibold text-white">
                      {formatShippingTierLabel(tier, isImported)}
                    </span>
                  )}
                </div>
              )}
              {reservePriceColumn && shownUnit === null && <div className="w-28 shrink-0" />}
              {shownUnit !== null && (
                <div className={reservePriceColumn ? 'w-28 shrink-0 whitespace-nowrap text-right' : 'text-right'}>
                  <p className="text-sm font-semibold text-neutral-800">
                    {(formatReal(shownUnit) as string) || `R$ ${shownUnit}`}
                    <span className="text-xs font-normal text-neutral-500"> /un</span>
                  </p>
                  {shownTotal !== null && (
                    <p className="text-xs text-neutral-500">
                      Total: {(formatReal(shownTotal) as string) || `R$ ${shownTotal}`}
                    </p>
                  )}
                </div>
              )}
            </Wrapper>
          )
        })}
      </div>
      {isCapped && (
        <p className="mt-2 text-xs text-neutral-500">
          <strong>{capLabel}</strong>
        </p>
      )}
      {showBrandNote && (
        <p className="mt-2 text-xs text-neutral-500">*Condição válida apenas para produtos da marca.</p>
      )}
    </div>
  )
}
